import DataDumpGetter from "segments/DataDumpGetter/DataDumpGetter";
import {
  useMetricsForWorkSchedule,
  useReportConfig,
} from "./FlightConfig/useFlightConfig";
import { useForceRerenderOnIntervalIfCurrentRange } from "./useForceRerenderOnIntervalIfCurrentRange";
import {
  useFocusRange,
  useGetPageFromPath,
} from "utils/customHooks/genericHooks";
import { UPDATE_CUSTOM_DATA_DUMP_FOR_PROCESSES } from "providers/GlobalStateProvider";
import { useFocusRangeFromQuery } from "reports/components/Reports/reportUtils";
import { useFilterByFlightConfig } from "components/Dashboard/GroupMetrics/useVisibleMetrics";
import { useIsTrendChartExpandedHubletPage } from "pages/DashboardPages/dashboardPagesUtils";

/** we need to fetch custom aggregates if...
 *  - if we are on a /reports page -> if flightConfig.metrics contains any "overrideMachemetryIds"
 *  - if we are on a /dashboard page -> if flightConfig.reportConfig.eosReportMetricsConfig contains any "overrideMachemetryIds"
 */
export function useShouldFetchMidgardOverriddenMetrics() {
  const reportsMetrics = useAllReportMetrics();
  const dashboardMetrics = useMetricsForWorkSchedule();
  const { isReportsPage, isHubletPage } = useGetPageFromPath();

  const metricsOnThisPage: { overrideMachemetryIds?: string[] }[] =
    isReportsPage ? reportsMetrics : dashboardMetrics;
  const metricsWithCustomAggregates = metricsOnThisPage.filter(
    metric =>
      metric.overrideMachemetryIds && metric.overrideMachemetryIds.length > 0
  );

  const [areMetricsVisibleHubletPage] = useIsTrendChartExpandedHubletPage();

  const areMetricsHiddenHubletPage =
    isHubletPage && !areMetricsVisibleHubletPage;

  const shouldFetchMidgardOverriddenMetricsDataDumps =
    metricsWithCustomAggregates.length > 0 && !areMetricsHiddenHubletPage;
  return {
    shouldFetchMidgardOverriddenMetricsDataDumps,
    metricsWithCustomAggregates,
  };
}

function useAllReportMetrics() {
  const reportConfig = useReportConfig();
  const metricsConfig = reportConfig?.eosReportMetricsConfig;
  const allReportConfigMetrics = [
    ...(metricsConfig?.summaryMetricsRow1 ?? []),
    ...(metricsConfig?.summaryMetricsRow2 ?? []),
    ...(metricsConfig?.summaryMetricsInputList ?? []),
  ];
  const filterByFlightConfig = useFilterByFlightConfig();

  // filter metrics based on which page we're on
  const filteredMetrics = allReportConfigMetrics.filter(filterByFlightConfig);
  return filteredMetrics;
}

/** fetch a separate DDA for a specified list of machemetryIds
 * (via flightConfig.metrics[N].overrideMachemetryIds)
 * e.g. "show a taggingPercent metric for machemetry ids ["example_d_001", "example_d_002"] only" */
export function CustomMetricsAggregatesGetter() {
  const {
    shouldFetchMidgardOverriddenMetricsDataDumps,
    metricsWithCustomAggregates,
  } = useShouldFetchMidgardOverriddenMetrics();
  const allOverrideMachemetryIdsLists = metricsWithCustomAggregates
    .map(metric => metric.overrideMachemetryIds?.sort())
    .reduce<
      string[][]
    >((acc, overrideMachemetryIds) => [...acc, ...(overrideMachemetryIds ? [overrideMachemetryIds] : [])], []);
  return shouldFetchMidgardOverriddenMetricsDataDumps ? (
    <CustomAggregatesGetter {...{ allOverrideMachemetryIdsLists }} />
  ) : null;
}

function CustomAggregatesGetter({
  allOverrideMachemetryIdsLists,
}: {
  allOverrideMachemetryIdsLists: string[][];
}) {
  const randKey = useForceRerenderOnIntervalIfCurrentRange();
  const uniqueOverrideMachemetryIdsLists = allOverrideMachemetryIdsLists.reduce<
    string[][]
  >((acc, machIds, idx) => {
    const alreadyFound =
      idx !== 0 &&
      acc.find(
        prevMachIds => JSON.stringify(prevMachIds) === JSON.stringify(machIds)
      );
    return [...acc, ...(alreadyFound ? [] : [machIds])];
  }, []);

  return (
    <FetchCustomAggregates
      key={randKey}
      uniqueOverrideMachemetryIdsLists={uniqueOverrideMachemetryIdsLists}
    />
  );
}
function FetchCustomAggregates({ uniqueOverrideMachemetryIdsLists }) {
  const focusRangeDashboardPages = useFocusRange();
  const { isReportsPage } = useGetPageFromPath();
  // on reports page, focus range is in the url
  const focusRangeFromQuery = useFocusRangeFromQuery();
  const focusRange = isReportsPage
    ? focusRangeFromQuery
    : focusRangeDashboardPages;

  // We only add 1 second to `lt` if it's the same time as `ge`.
  // There have been issues where the time is 1 second greater than 1 day and 24-hour reports stop working.
  const lessThan = focusRange?.lt ?? 0;
  const adjustedLessThan =
    (focusRange?.ge ?? 0) < lessThan ? lessThan : lessThan + 1;

  return (
    <>
      {uniqueOverrideMachemetryIdsLists.map(machIds => (
        <DataDumpGetter
          key={machIds.join(",")}
          timeRange={{ ...focusRange, lt: adjustedLessThan } as any}
          processIds={machIds}
          dataDumpAggType={UPDATE_CUSTOM_DATA_DUMP_FOR_PROCESSES}
          additionalPayload={{ overrideMachemetryIds: machIds.join(",") }}
        />
      ))}
    </>
  );
}
